/**
 * Structure-preserving revert for KR (Boost Keyword Relevance) rewrites.
 *
 * The backend only applies KR rewrites to elements with one of these shapes
 * (richer elements are excluded as `rich_inline_unsupported`, and body-span
 * rewrites only target leaf nodes):
 *
 *   - plain leaf — no child elements; a text swap is lossless
 *   - leading label — a single <strong>/<b> child ending with ':' followed by
 *     plain suffix text; apply keeps the label node and replaces only the
 *     suffix
 *   - single inline wrapper — the element's only content is one
 *     span/em/strong/b/i/u/small/mark holding plain text; apply writes the
 *     text inside the wrapper
 *   - attribute-only (image alt / metadata) — handled by callers, no children
 *     involved
 *
 * The KR report's `originalText` is flattened plain text, so a naive
 * `textContent = original` revert destroys the label/wrapper markup the
 * backend deliberately preserved when applying the rewrite. These helpers
 * mirror the apply shapes to restore the original text into the surviving
 * structure, and fall back to the plain-text flatten for any shape they
 * don't recognize.
 */

const LEADING_LABEL_TAGS = new Set(['STRONG', 'B']);
const SAFE_WRAPPER_TAGS = new Set(['SPAN', 'EM', 'STRONG', 'B', 'I', 'U', 'SMALL', 'MARK']);

const isJsonElement = (child: any): boolean => child != null && typeof child === 'object';

/** Flattened text of a JSON node (strings concatenated depth-first). */
const jsonNodeText = (node: any): string => {
  if (typeof node === 'string') return node;
  if (!isJsonElement(node) || !Array.isArray(node.children)) return '';
  return node.children.map(jsonNodeText).join('');
};

interface RevertShape {
  /** 'leading-label': keep the label element, replace the suffix text. */
  kind: 'leading-label' | 'single-wrapper';
  suffix?: string;
}

/**
 * Classify a reverted element against the shapes the backend can produce.
 * `wrapperTag` is uppercase; `wrapperText`/`afterHasText`/`wrapperHasElementChildren`
 * describe the single element child and its text siblings. Returns null when
 * the shape is unrecognized and the caller should flatten.
 */
const classifyRevertShape = (
  originalText: string,
  wrapperTag: string,
  wrapperText: string,
  wrapperHasElementChildren: boolean,
  beforeHasText: boolean,
  afterHasText: boolean
): RevertShape | null => {
  if (beforeHasText) return null;
  const original = (originalText || '').trim();
  const labelText = (wrapperText || '').trim();
  if (
    LEADING_LABEL_TAGS.has(wrapperTag) &&
    labelText &&
    /:$/.test(labelText) &&
    afterHasText &&
    original.startsWith(labelText)
  ) {
    // Keep the suffix verbatim (no trim, no reconstructed separator): the
    // original may legitimately have no space after the label, or a newline
    // instead — normalizing here would alter the reverted document.
    return { kind: 'leading-label', suffix: original.slice(labelText.length) };
  }
  if (SAFE_WRAPPER_TAGS.has(wrapperTag) && !wrapperHasElementChildren && !afterHasText) {
    return { kind: 'single-wrapper' };
  }
  return null;
};

/**
 * Compute the reverted `children` array for a JSON element node
 * ({tag, attributes, children: Array<node|string>}, as produced by
 * htmlToJson). Preserves a leading-label or single-inline-wrapper child;
 * otherwise returns the flattened `[originalText]` (today's behavior, which
 * is already correct for plain leaves).
 */
export function revertKrElementJsonChildren(node: any, originalText: string): any[] {
  const flattened = [originalText || ''];
  const children = Array.isArray(node?.children) ? node.children : [];
  const elementChildren = children.filter(isJsonElement);
  if (elementChildren.length !== 1) return flattened;
  const wrapper = elementChildren[0];
  const wrapperTag = String(wrapper.tag || '').toUpperCase();
  const wrapperIndex = children.indexOf(wrapper);
  const before = children.slice(0, wrapperIndex);
  const after = children.slice(wrapperIndex + 1);
  // Siblings must all be plain text (CB/comment-ish objects were filtered
  // into elementChildren above, so this only rejects exotic child values).
  if (before.some((child: any) => typeof child !== 'string') || after.some((child: any) => typeof child !== 'string')) {
    return flattened;
  }
  const shape = classifyRevertShape(
    originalText,
    wrapperTag,
    jsonNodeText(wrapper),
    (Array.isArray(wrapper.children) ? wrapper.children : []).some(isJsonElement),
    before.some((child: string) => child.trim() !== ''),
    after.some((child: string) => child.trim() !== '')
  );
  if (!shape) return flattened;
  if (shape.kind === 'leading-label') {
    return shape.suffix ? [wrapper, shape.suffix] : [wrapper];
  }
  return [...before, { ...wrapper, children: [originalText || ''] }, ...after];
}

/**
 * Revert a live DOM element to its original text, preserving a
 * leading-label or single-inline-wrapper child. Falls back to
 * `textContent = originalText` (today's behavior) for unrecognized shapes.
 */
export function revertKrDomNode(node: HTMLElement, originalText: string): void {
  const flatten = () => {
    node.textContent = originalText || '';
  };
  const elementChildren = Array.from(node.children) as HTMLElement[];
  if (elementChildren.length !== 1) return flatten();
  const wrapper = elementChildren[0];
  let beforeHasText = false;
  let afterHasText = false;
  let seenWrapper = false;
  for (const child of Array.from(node.childNodes)) {
    if (child === wrapper) {
      seenWrapper = true;
      continue;
    }
    // Anything other than a text node (comments, CDATA…) is a shape the
    // backend never rewrites — flatten rather than guess.
    if (child.nodeType !== 3) return flatten();
    if ((child.textContent || '').trim()) {
      if (seenWrapper) afterHasText = true;
      else beforeHasText = true;
    }
  }
  const shape = classifyRevertShape(
    originalText,
    wrapper.tagName.toUpperCase(),
    wrapper.textContent || '',
    wrapper.children.length > 0,
    beforeHasText,
    afterHasText
  );
  if (!shape) return flatten();
  if (shape.kind === 'leading-label') {
    while (wrapper.nextSibling) wrapper.nextSibling.remove();
    if (shape.suffix) {
      node.appendChild(node.ownerDocument.createTextNode(shape.suffix));
    }
    return;
  }
  wrapper.textContent = originalText || '';
}
